import PropTypes from 'prop-types'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useAppData from 'app/services/hooks/useAppData'
import getSocialNetworkShareLink from 'app/util/getSocialNetworkShareLink'
import Button from 'components/core/Button/Button'
import Tooltip from 'components/interface/Tooltip/Tooltip'
import copyToClipboard from 'lib/util/copyToClipboard'
import styles from './ButtonShare.module.scss'

/**
 * The `ButtonShare` component
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
function ButtonShare (props) {
  const { children, color, size, type } = props
  const appData = useAppData()
  const [hasCopiedLink, setHasCopiedLink] = useState(false)
  const timeoutCopy = useRef()
  const content = useMemo(
    () =>
      appData?.global?.share || {
        networks: []
      },
    [appData]
  )
  /**
   * Memo: The networks, with the share url preconfigured
   */
  const networks = useMemo(
    () =>
      content.networks.map(network => ({
        ...network,
        url: getSocialNetworkShareLink(network.type),
        label: {}
      })),
    [content]
  )

  /**
   * The copy link label
   */
  const labelCopyLink = useMemo(
    () =>
      hasCopiedLink ? content.label.copyLinkCopied : content.label.copyLink,
    [hasCopiedLink, content]
  )

  /**
   * Handle copying the link
   */
  const handleCopyLink = useCallback(() => {
    clearTimeout(timeoutCopy)

    const url = getSocialNetworkShareLink('link')
    const success = copyToClipboard(url)

    if (!success) {
      setHasCopiedLink(false)

      return
    }

    setHasCopiedLink(true)

    timeoutCopy.current = setTimeout(() => {
      setHasCopiedLink(false)
    }, 1000)
  }, [])

  /**
   * Effect: On unmount
   */
  useEffect(
    () => () => {
      clearTimeout(timeoutCopy.current)
    },
    []
  )

  return (
    <Button
      type={type}
      icon="share"
      color={color}
      size={size}
      useTooltip={true}
      tooltipMode="button"
      title="Share this"
      tooltipContent={
        <Tooltip
          color="b"
          className={styles.tooltip}
          size="medium"
          delayShow={0}
          distance={35}
        >
          <Tooltip.Header>{content?.title}</Tooltip.Header>
          <Tooltip.Body>
            <ul className={styles.options}>
              {networks.map(({ icon, title, url }, i) => (
                <li key={i}>
                  <Button
                    color="i"
                    icon={icon}
                    Tag="a"
                    href={url}
                    target="_blank"
                  >
                    {title}
                  </Button>
                </li>
              ))}
              <li>
                <Button color="i" icon="link" onClick={handleCopyLink}>
                  {labelCopyLink}
                </Button>
              </li>
            </ul>
          </Tooltip.Body>
        </Tooltip>
      }
    >
      {children}
    </Button>
  )
}

/** @type {object} */
ButtonShare.propTypes = {
  children: PropTypes.node,
  color: PropTypes.string,
  size: PropTypes.string,
  type: PropTypes.string
}

/** @type {object} */
ButtonShare.defaultProps = {
  children: null,
  color: 'c',
  label: '',
  size: 'standard',
  type: 'symbol'
}

// Memoize
export default memo(ButtonShare)
